import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import YouTubeEmbed from "../../YouTubeEmbed/YouTubeEmbed";

const placeholderImage = "/images/imagePlaceholder.webp";
const placeholderVideo = "/images/videoPlaceholder.webp";

// Utility function to generate a slug from the news heading
const generateSlug = (text) => {
  return text
    ?.toLowerCase()
    .replace(/\s+/g, "-")       // Replace spaces with hyphens
    .replace(/[^\w-]+/g, "")    // Remove non-alphanumeric characters
    .replace(/--+/g, "-")       // Replace multiple hyphens with a single one
    .replace(/^-+|-+$/g, "");   // Trim hyphens from start/end
};

const isHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str);
const truncateHtml = (html, wordLimit) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";

  const words = text.trim().split(/\s+/).slice(0, wordLimit).join(" ");
  return `${words}...`;
};

// Function to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  return match && match[2].length === 11 ? match[2] : null;
};

const NewsCard = ({ item }) => {
  const { _id, news_heading, coverImage, time, news_content, media_type } = item;
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const slug = generateSlug(news_heading);
  const internalLink = `/news/${_id}/${slug}`;
  
  // Determine if the media is a YouTube video
  const youtubeVideoId = media_type === "youtube" ? getYouTubeVideoId(coverImage) : null;

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
    
    setIsMuted(!isMuted);
  };

  // Media element rendering based on type
  const renderMedia = () => {
    if (!coverImage) {
      return (
        <div className="h-[220px] xs:h-[250px] sm:h-[280px] md:h-[310px] lg:h-[24rem] w-full bg-gray-200 flex items-center justify-center rounded-t-lg flex-shrink-0">
          <p className="text-gray-500 text-xs xs:text-sm sm:text-base">No media available</p>
        </div>
      );
    }

    if (media_type === "youtube" && youtubeVideoId) {
      return (
        <div className="h-[220px] xs:h-[250px] sm:h-[280px] md:h-[310px] lg:h-[24rem] w-full">
          <YouTubeEmbed 
            videoId={youtubeVideoId}
            isMuted={isMuted}
            onToggleMute={toggleMute}
          />
        </div>
      );
    } else if (media_type === "video") {
      return (
        <div className="h-[220px] xs:h-[250px] sm:h-[280px] md:h-[310px] lg:h-[24rem] w-full relative rounded-t-lg overflow-hidden flex-shrink-0">
          <video 
            ref={videoRef}
            src={coverImage} 
            className="w-full h-full object-cover"
            autoPlay
            muted={isMuted}
            loop
            playsInline
          >
            Your browser does not support the video tag.
          </video>
          <button 
            className="absolute bottom-2 xs:bottom-3 sm:bottom-4 right-2 xs:right-3 sm:right-4 bg-black bg-opacity-50 text-white p-1.5 xs:p-2 rounded-full z-10 hover:bg-opacity-70 transition-all"
            onClick={toggleMute}
          >
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="xs:w-5 xs:h-5 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                <line x1="17" y1="3" x2="8" y2="12"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="xs:w-5 xs:h-5 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 1L6 5H2v6h4l4 4V1z"></path>
                <path d="M14 5a4 4 0 0 1 0 6"></path>
                <path d="M17 3a7 7 0 0 1 0 10"></path>
              </svg>
            )}
          </button>
        </div>
      );
    } else {
      // Default to image
      return (
        <div className="h-[220px] xs:h-[250px] sm:h-[280px] md:h-[310px] lg:h-[24rem] w-full rounded-t-lg overflow-hidden flex-shrink-0 relative">
          <img
            src={mediaLoaded ? coverImage : placeholderImage}
            alt="AKIYA2.0 news"
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onLoad={() => setMediaLoaded(true)}
            onError={() => {
              // Fallback to placeholder if image fails to load
              if (mediaLoaded) setMediaLoaded(false);
            }}
          />
          {/* Loading overlay */}
          {!mediaLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-t-lg"></div>
          )}
        </div>
      );
    }
  };

  // Prevent link navigation when clicking mute button or video controls
  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('iframe') || e.target.closest('.cursor-pointer')) {
      e.preventDefault();
    }
  };

  return (
    <Link
      to={internalLink}
      className="flex shadow-[rgba(0,0,0,0.05)_0px_0px_20px_0px] hover:shadow-[rgba(0,0,0,0.15)_0px_0px_30px_0px] w-full justify-around cursor-pointer rounded-lg overflow-hidden bg-white transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1 group h-full"
      onClick={handleCardClick}
    >
      <div className="flex flex-col w-full h-full">
        {/* Fixed height media section */}
        {renderMedia()}

        {/* Flexible content section with generous spacing */}
        <div className="flex flex-col flex-grow mx-3 xs:mx-4 sm:mx-5 w-full px-2 xs:px-3 py-4 xs:py-5 sm:py-6">
          {/* Responsive Time with bottom margin */}
          <p className="text-[--medium-sea-green] text-xs xs:text-sm sm:text-base font-medium mb-3 xs:mb-4 flex-shrink-0">
            {time}
          </p>
          
          {/* Heading with natural flow and generous margins */}
          <h2 className="font-bold text-base xs:text-lg sm:text-xl md:text-xl lg:text-xl mb-4 xs:mb-5 sm:mb-6 leading-relaxed text-gray-800 group-hover:text-[--medium-sea-green] transition-colors duration-300 flex-shrink-0">
            {news_heading}
          </h2>
          
          {/* Content with natural flow */}
          <div className="flex-grow">
            {isHtml(news_content) ? (
              <div
                className="font-light text-xs xs:text-sm sm:text-base text-[--dark-grey] mr-2 xs:mr-3 sm:mr-5 break-words leading-relaxed"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
                dangerouslySetInnerHTML={{
                  __html: truncateHtml(news_content, 45),
                }}
              />
            ) : (
              <p className="font-light text-xs xs:text-sm sm:text-base text-[--dark-grey] mr-2 xs:mr-3 sm:mr-5 break-words leading-relaxed"
                 style={{
                   display: '-webkit-box',
                   WebkitLineClamp: 4,
                   WebkitBoxOrient: 'vertical',
                   overflow: 'hidden'
                 }}>
                {news_content.split(" ").slice(0, 45).join(" ")}...
              </p>
            )}
          </div>
          
          {/* Bottom spacer to ensure consistent card bottoms */}
          <div className="mt-auto pt-4 xs:pt-5 sm:pt-6"></div>
        </div>
      </div>
    </Link>
  );
};

export default NewsCard;